/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type MaterialSymbolName } from '@/components/icon/glyphs';
import { STATUS_ICON, type StatusType } from '@/lib/feedback/status';

/**
 * Toast type taxonomy and its presentation map (CRCB2B-641).
 *
 * The shared feedback vocabulary — `info | success | warning | error` — used
 * identically by Toast, Inline Alert and Alert Banner. `alert` appears in
 * CRCB2B-641's type list but is NOT a type: no Figma treatment defines one and the
 * name collides with Alert the primitive, so it was confirmed a drafting synonym
 * (styleguide-notifications design D1).
 *
 * Presentation is a RECORD, not a `switch`. The AC requires the type set be
 * "extensible… without restructuring the component", so adding a fifth type must
 * cost one entry here and nothing else. The previous three-arm `switch` was
 * exactly the structure that requirement rules out.
 *
 * This lives in its own module rather than in `index.ts` because `app-toaster.tsx`
 * consumes the icon names and `index.ts` re-exports `AppToaster` — importing the map
 * from `index.ts` would close a cycle.
 */
export type ToastType = StatusType;

/**
 * Sonner's typed emitters (`toast.info` etc.) rather than bare `toast()`. Each sets
 * `data-type="<type>"` on the rendered element, which is what lets
 * `src/theme/overrides/sonner.css` colour the icon and text per type — the toasts are
 * `unstyled`, so that attribute is the only hook the stylesheet has.
 */
export type SonnerMethod = 'info' | 'success' | 'warning' | 'error';

export interface ToastPresentation {
    /** Sonner emitter to call; also determines the rendered `data-type`. */
    method: SonnerMethod;
    /** Glyph from the shipped Material Symbols subset (`src/components/icon/glyphs.ts`). */
    icon: MaterialSymbolName;
    /**
     * Default auto-dismiss in ms, or `Infinity` to persist until dismissed. An
     * explicitly supplied duration always wins.
     */
    duration: number;
}

/**
 * Severity-graded defaults. The AC asks for timing that varies by severity and gives
 * no numbers, so these are ours pending design (styleguide-notifications Open
 * Question 4) — deliberately in one place so changing them is a single edit.
 *
 * `error` does not auto-dismiss: a failure the user never read is worse than a toast
 * that lingers. Callers who want a transient error pass an explicit duration.
 */
export const TOAST_PRESENTATION: Record<ToastType, ToastPresentation> = {
    info: { method: 'info', icon: STATUS_ICON.info, duration: 4000 },
    success: { method: 'success', icon: STATUS_ICON.success, duration: 4000 },
    warning: { method: 'warning', icon: STATUS_ICON.warning, duration: 6000 },
    error: { method: 'error', icon: STATUS_ICON.error, duration: Number.POSITIVE_INFINITY },
};

/** Neutral fallback for a type outside the map — never throw, never render untyped. */
export const FALLBACK_TOAST_TYPE: ToastType = 'info';

/**
 * Resolve a type to its presentation, falling back to neutral `info`. Takes a loose
 * string because the guard's whole purpose is types that escaped the compiler —
 * config, a CMS field, or a JS call site.
 */
export function resolveToastPresentation(type: string | undefined): ToastPresentation {
    // `Object.hasOwn`, not `?? fallback`: a bare index lookup walks the prototype chain, so
    // `type` of `'constructor'` or `'toString'` returns a FUNCTION — non-nullish, so `??`
    // happily passes it through and the toast is raised against `Object`. An own-property
    // check is the only form that treats every non-type alike.
    if (type != null && Object.hasOwn(TOAST_PRESENTATION, type)) {
        return TOAST_PRESENTATION[type as ToastType];
    }
    return TOAST_PRESENTATION[FALLBACK_TOAST_TYPE];
}
